import React, { useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import { Box, Chip, TextField, Tooltip, Typography } from "@mui/material";

import { isEmailValid } from "../../Support/utils";

type Props = {
  emails: string[];
  handleChange: (emails: string[]) => void;
  isLegacyFlow?: boolean;
};

function EmailChipsInput({ emails, handleChange, isLegacyFlow }: Props) {
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation("services");

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (["Enter", ",", " ", "Tab"].includes(e.key)) {
      const trimmedValue = inputValue.trim().replace(/,$/, "");
      if (trimmedValue && isEmailValid(trimmedValue)) {
        e.preventDefault();
        if (!emails.includes(trimmedValue)) {
          handleChange([...emails, trimmedValue]);
        }
        setInputValue("");
      }
    } else if ((e.key === "Backspace" || e.key === "Delete") && inputValue === "" && emails.length > 0) {
      e.preventDefault();
      handleChange(emails.slice(0, -1));
    }
  };

  const handleDelete = (emailToDelete: string) => {
    handleChange(emails.filter((email) => email !== emailToDelete));
  };

  const handleBoxClick = () => {
    inputRef.current?.focus();
  };

  const formatChipLabel = (email: string) => (email.length > 25 ? `${email.substring(0, 25)}...` : email);

  return (
    <Box sx={{ position: "relative", mt: 2 }}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          position: "absolute",
          top: -10,
          left: 10,
          px: 0.5,
          bgcolor: isLegacyFlow ? "background.paper" : "general.backgroundDark",
          zIndex: 1,
        }}
      >
        {t("DetailsStep.additionalCCs")}
      </Typography>
      <Box
        sx={{
          py: 1.3,
          px: 2,
          border: "1px solid #555",
          borderRadius: 1,
          position: "relative",
          cursor: "text",
          "&:hover": {
            borderColor: "text.primary",
          },
        }}
        onClick={handleBoxClick}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            alignItems: "center",
          }}
        >
          {emails.map((email) => (
            <Tooltip title={email} key={email}>
              <Chip
                label={formatChipLabel(email)}
                size="small"
                sx={{ zIndex: 10 }}
                onDelete={() => {
                  handleDelete(email);
                }}
              />
            </Tooltip>
          ))}
          <TextField
            variant="standard"
            inputRef={inputRef}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            slotProps={{
              input: {
                disableUnderline: true,
                sx: { mt: 0.5 },
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default EmailChipsInput;
